import type { FEATURE_FLAGS_ALL_VALUES } from '@/helpers/feature-flag/model/constants';

import delay from '@/helpers/delay';
import { getFeatureFlagOverride, preserveAllFeatureFlags } from '@/helpers/feature-flag/api/dev-testing';
import { type FEATURE_FLAGS_VALUE, getFeatureFlagDefault } from '@/helpers/feature-flag/model/constants';
import { isServerSide } from '@/helpers/isServerSide';
import { log } from '@/helpers/util/logger';

import type { FeatureFlagProvider, FeatureFlagResult } from './Provider';

let globalClient: FeatureFlagClient | undefined;
export const getFeatureFlagClientSingleton = (): FeatureFlagClient => {
  if (!globalClient) {
    globalClient = new FeatureFlagClient();
  }
  return globalClient;
};

type CacheEntry = {
  reason: 'SSR' | 'PROVIDER' | 'CACHED' | 'OVERRIDE' | 'ERROR';
  timestamp: number;
  value: boolean;
};

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 mins

export class FeatureFlagClient {
  private tag = 'FeatureFlagClient';

  private provider?: FeatureFlagProvider;
  private cache = new Map<FEATURE_FLAGS_VALUE, CacheEntry>();
  private cacheTtl = CACHE_TTL_MS;

  private initPromise?: Promise<void>;

  get isInitialized() {
    return this.initPromise === undefined && this.provider !== undefined;
  }

  async setProvider(
    provider: FeatureFlagProvider,
    initConfigArgs?: unknown,
    options?: {
      featureFlagsSSR?: FEATURE_FLAGS_ALL_VALUES;
    },
  ): Promise<void> {
    const isSameProvider = this.provider?.tag === provider.tag;
    if (isSameProvider) {
      if (this.initPromise) await this.initPromise;
      return;
    }

    if (this.initPromise) await this.initPromise;

    const isDifferentProvider = this.provider && this.provider.tag !== provider.tag;
    if (isDifferentProvider) {
      this.clearCache();
    }

    this.provider = provider;

    if (provider.initialize) {
      this.initPromise = provider.initialize(initConfigArgs).finally(() => {
        this.initPromise = undefined;
      });
      await this.initPromise;
    }

    if (!isServerSide() && options?.featureFlagsSSR) {
      this.fillCacheFromSSR(options.featureFlagsSSR);
    }
  }

  private fillCacheFromSSR(featureFlagsSSR: FEATURE_FLAGS_ALL_VALUES): void {
    for (const [flagKey, flagValue] of Object.entries(featureFlagsSSR)) {
      this.setToCache(flagKey as FEATURE_FLAGS_VALUE, {
        reason: 'SSR',
        timestamp: Date.now(),
        value: flagValue,
      });

      preserveAllFeatureFlags(flagKey as FEATURE_FLAGS_VALUE, flagValue);
    }
  }

  async getBooleanFlag(flagKey: FEATURE_FLAGS_VALUE): Promise<FeatureFlagResult> {
    if (!this.isInitialized) {
      if (this.initPromise) {
        await this.initPromise;
      } else {
        // SSR Bottleneck:
        // flag in the layout.tsx will wait until all flags will be fetched
        await delay(0);
        return this.getBooleanFlag(flagKey);
      }
    }

    const defaultValue = getFeatureFlagDefault(flagKey);

    if (!this.provider) {
      const errorMessage = `${this.tag}: no provider for key ${flagKey}`;

      log(errorMessage);

      return { error: errorMessage, reason: 'ERROR', value: defaultValue };
    }

    const override = getFeatureFlagOverride(flagKey);
    if (override !== null) {
      return { reason: 'OVERRIDE', value: override };
    }

    const cached = this.getFromCache(flagKey);
    if (cached) {
      return { reason: 'CACHED', value: cached.value };
    }

    try {
      const result = await this.provider.getBooleanFlag(flagKey, defaultValue);

      if (result.reason === 'PROVIDER') {
        this.setToCache(flagKey, {
          reason: result.reason,
          timestamp: Date.now(),
          value: result.value,
        });
      }
      preserveAllFeatureFlags(flagKey, result.value);

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : `${this.tag}: error on getting flag ${flagKey}`;

      log(errorMessage);

      return {
        error: errorMessage,
        reason: 'ERROR',
        value: defaultValue,
      };
    }
  }

  public getCachedValue(flagKey: FEATURE_FLAGS_VALUE): boolean | undefined {
    const cached = this.getFromCache(flagKey);

    return cached?.value;
  }

  private getFromCache(flagKey: FEATURE_FLAGS_VALUE): CacheEntry | undefined {
    const entry = this.cache.get(flagKey);

    if (!entry) {
      return undefined;
    }

    if (this.removeExpiredFlagsFromCache(flagKey)) {
      return undefined;
    }

    return entry;
  }

  private removeExpiredFlagsFromCache(flagKey: FEATURE_FLAGS_VALUE): boolean {
    const entry = this.cache.get(flagKey);
    if (!entry) {
      return false;
    }

    if (Date.now() - entry.timestamp > this.cacheTtl) {
      this.cache.delete(flagKey);
      return true;
    }

    return false;
  }

  private setToCache(flagKey: FEATURE_FLAGS_VALUE, entry: CacheEntry): void {
    this.cache.set(flagKey, entry);
  }

  clearCache(): void {
    this.cache.clear();
  }
}
